import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { FastTrackRouteQueryParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Color, Modal, Utilities } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqLoadingModal, ModalService } from '@lsq-frontend/common-ui';
import { AnalyticsEvent } from '../../../../constants/analytics.constants';

import { InvoiceFileUpload } from '../../invoices.models';
import { InvoiceBatchService } from '../invoice-batch.service';
import { UploadInvoicesService } from '../upload-invoices.service';

@Component({
    selector: 'mobile-upload-invoices',
    template: `
        <div class="mobile-upload-invoices">
            <h2 class="page-title font-weight-bold text-center">Click below to get started</h2>
            <div class="upload-method-icons">
                <lsq-icon
                    [type]="icons.Camera"
                    [height]="cameraIconDimensions.height"
                    [width]="cameraIconDimensions.width"
                    [color]="color.GrayDark"
                >
                </lsq-icon>
                <div class="divider-line">
                    <div class="divider-line-text">or</div>
                </div>
                <lsq-icon
                    [type]="icons.CameraRoll"
                    [height]="cameraRollIconDimensions.height"
                    [width]="cameraRollIconDimensions.width"
                    [color]="color.GrayDark"
                >
                </lsq-icon>
            </div>
            <div class="upload-button" (click)="handleUploadBtnClick()">
                <lsq-icon
                    [type]="icons.Upload"
                    [height]="uploadIconDimensions.height"
                    [width]="uploadIconDimensions.width"
                    [color]="color.White"
                >
                </lsq-icon>
            </div>
            <p class="foot-note text-center">
                * uploaded files will appear on the page where you entered your phone number once
                the upload has completed
            </p>
        </div>
    `,
    styleUrls: ['./mobile-upload-invoices.component.scss']
})
export class MobileUploadInvoicesComponent implements OnInit {
    public fileInputElement: HTMLInputElement;
    public invoiceFileUploads: InvoiceFileUpload[] = [];
    public icons = Icons;
    public color = Color;
    public hasFailed = false;
    public cameraIconDimensions = {
        height: 70,
        width: 70
    };
    public cameraRollIconDimensions = {
        height: 75,
        width: 75
    };
    public uploadIconDimensions = {
        height: 50,
        width: 50
    };

    constructor(
        private _route: ActivatedRoute,
        private _uploadInvoicesService: UploadInvoicesService,
        private _invoiceBatchService: InvoiceBatchService,
        private _modalService: ModalService,
        private _analyticsService: AnalyticsService
    ) {}

    public ngOnInit(): void {
        this._initUploader();

        this._uploadInvoicesService.invoiceFileUploadsRef.fileUploadsStream.subscribe(
            (fileUploads) => {
                this.invoiceFileUploads = fileUploads;

                if (
                    fileUploads.length &&
                    fileUploads.every((fileUpload) => fileUpload.isUploadStatusComplete)
                ) {
                    this.handleInvoiceUploadsCompleted();
                } else if (
                    !this.hasFailed &&
                    fileUploads.length &&
                    fileUploads.some((fileUpload) => fileUpload.hasFailed)
                ) {
                    this.handleInvoiceUploadsFailure();
                }
            }
        );
    }

    // Uploader methods.
    public get acceptContentTypes(): string {
        return this._uploadInvoicesService.allowedContentTypesForInvoices.join(',');
    }

    public handleInvoiceUploadsCompleted(): void {
        this._analyticsService.triggerEventWithPayload(AnalyticsEvent.MobileUploadFilesUploaded, {
            count: this.invoiceFileUploads.map((fileUpload) => fileUpload.isUploadStatusComplete)
                .length
        });
        this._modalService.closeModal();

        Utilities.executeAfterRender(() => {
            this._modalService.showModal(
                new Modal({
                    id: 'upload-confirmation',
                    title: 'Success!',
                    bodyHtml: `<p>Your ${
                        this.invoiceFileUploads.length > 1 ? 'images have' : 'image has'
                    } been sent.
                    The files will appear on the Upload Invoices page of Dashboard within a few seconds.
                    <br><br>
                    Feel free to upload more images if needed.</p>`,
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Ok'
                })
            );
        });
    }

    public handleInvoiceUploadsFailure(): void {
        this._modalService.closeModal();
        this.hasFailed = true;

        Utilities.executeAfterRender(() => {
            this._modalService.showModal(
                new Modal({
                    id: 'upload-error',
                    title: 'Upload failed',
                    bodyText: `Your ${
                        this.invoiceFileUploads.length > 1 ? 'images have' : 'image has'
                    } failed to upload.
                    Please ensure that the invoice batch on Dashboard has not already been submitted or canceled and try again.`,
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Ok'
                })
            );
        });
    }

    // Event handlers.
    public handleUploadBtnClick(): void {
        if (!!this.fileInputElement) {
            this.fileInputElement.click();
        }
    }

    // Initialize the uploader.
    private _initUploader(): void {
        this._uploadInvoicesService.reset();
        this._uploadInvoicesService.shouldFetchPreview = false;
        this._uploadInvoicesService.onBeforeInvoiceFileUploadsExecuted = (fileUploads) => {
            if (fileUploads.length) {
                this.hasFailed = false;
                let title = 'Your image is being uploaded...';
                if (fileUploads.length > 1) {
                    title = 'Your images are being uploaded...';
                }
                this._modalService.showModal(
                    new LsqLoadingModal(
                        title,
                        'Please ensure your device does not go to sleep and refrain from leaving the page/browser until the upload completes.'
                    )
                );
            }
        };

        const queryParamMap = this._route.snapshot.queryParamMap;
        // Set the batch key for the upload service based on the route query param.
        this._invoiceBatchService.invoiceBatchKey = queryParamMap.get(
            FastTrackRouteQueryParams.InvoiceBatchKey
        );

        this.invoiceFileUploads = [];
        const { inputElement } = this._uploadInvoicesService.initInvoiceFileUploadsStream();
        this.fileInputElement = inputElement;
    }
}
